/**
 * Web Push 구독/해지 헬퍼
 * - 브라우저 Notification.requestPermission + PushManager 관리
 * - 백엔드에 구독 정보 저장/삭제
 */
import { get, post, apiClient } from '@/api/client';
import { toast } from '@/store/toastStore';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function subscribePush(): Promise<boolean> {
  if (!isPushSupported()) {
    toast.error('이 브라우저는 푸시 알림을 지원하지 않아요.');
    return false;
  }

  // 권한 요청
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    toast.error('알림 권한을 허용해 주세요.');
    return false;
  }

  // VAPID 공개키 조회
  const { key } = await get<{ key: string | null }>('/notifications/vapid-public');
  if (!key) {
    toast.error('서버 푸시 설정이 준비되지 않았어요.');
    return false;
  }

  // Service Worker 대기
  const reg = await navigator.serviceWorker.ready;

  // 기존 구독 재사용 또는 신규 구독
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    });
  }

  const json = sub.toJSON();
  await post('/notifications/push/subscribe', {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys!.p256dh, auth: json.keys!.auth },
    deviceName: navigator.userAgent.slice(0, 100),
  });

  return true;
}

export async function unsubscribePush(): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await apiClient.delete('/notifications/push/unsubscribe', { data: { endpoint: sub.endpoint } });
    await sub.unsubscribe();
  }
}
